import { useCallback, useEffect, useState } from "react";
import { Banknote, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API, inr } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const thisMonth = () => new Date().toISOString().slice(0, 7);

function Stat({ label, value, strong, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`mt-1 text-xl font-bold ${strong ? "text-emerald-700" : "text-slate-900"}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function PayslipGrid({ p }) {
  if (!p) return <div className="py-6 text-center text-sm text-slate-500" data-testid="payslip-empty">Select an employee to view their payslip.</div>;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3" data-testid="payslip-grid">
      <Stat label="Base Salary" value={inr(p.base_salary)} testid="payslip-base" />
      <Stat label="Working Days" value={p.working_days} testid="payslip-working-days" />
      <Stat label="Present" value={p.present} testid="payslip-present" />
      <Stat label="Absent" value={p.absent} testid="payslip-absent" />
      <Stat label="Half Days" value={p.half_days ?? 0} testid="payslip-half-days" />
      <Stat label="Paid Leave Used" value={p.paid_leave_used ?? 0} testid="payslip-paid-leave" />
      <Stat label="Daily Rate" value={inr(p.daily_rate)} testid="payslip-daily-rate" />
      <Stat label="Deduction" value={p.deduction ? `- ${inr(p.deduction)}` : "—"} testid="payslip-deduction" />
      <Stat label="Net Payable (gross)" value={inr(p.net_payable)} strong testid="payslip-net" />
    </div>
  );
}

export default function Payroll() {
  const [month, setMonth] = useState(thisMonth());
  const [empSel, setEmpSel] = useState("");
  const [slip, setSlip] = useState(null);
  const [slips, setSlips] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [savingId, setSavingId] = useState("");
  const [quota, setQuota] = useState(0);

  const saveQuota = async () => {
    try {
      await API.put("/payroll/config", { paid_leave_quota: Number(quota) || 0 });
      toast.success("Paid leave quota saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const load = useCallback(() => {
    API.get("/payroll/payslips", { params: { month } }).then((r) => setSlips(r.data)).catch(() => {});
    API.get("/payroll/salaries").then((r) => setSalaries(r.data)).catch(() => {});
    API.get("/payroll/config").then((r) => setQuota(r.data.paid_leave_quota || 0)).catch(() => {});
  }, [month]);
  useEffect(load, [load]);

  useEffect(() => {
    if (!empSel) { setSlip(null); return; }
    API.get("/payroll/payslip", { params: { month, user_id: empSel } }).then((r) => setSlip(r.data)).catch(() => setSlip(null));
  }, [empSel, month]);

  const saveSalary = async (row) => {
    setSavingId(row.user_id);
    try {
      await API.put(`/payroll/salary/${row.user_id}`, { base_salary: Number(row.base_salary) || 0 });
      toast.success(`Salary saved for ${row.employee}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingId("");
    }
  };

  return (
    <div className="max-w-6xl space-y-6" data-testid="payroll-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Payroll</h1>
        <p className="text-sm text-slate-500">Company 2 team — monthly salary & payslips from attendance (Admin / Accountant only)</p>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="gross-pay-disclaimer">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <span className="font-semibold">Gross-pay calculator.</span> Figures do not include statutory deductions (PF, ESI, TDS) — review before treating Net Payable as a final payable amount.
            <span className="mt-1 block text-xs">Paid-leave quota: the first N absent days per month are paid (set below the payslips table title); beyond that, absences deduct at the daily rate and half days at half rate. Working days and holidays are configurable under Attendance → Shift Settings.</span>
          </div>
        </div>
      </div>

      <Card className="border-slate-200" data-testid="payslip-lookup-card">
        <CardHeader><CardTitle className="text-base">Employee Payslip</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Banknote className="h-4 w-4 text-slate-400" />
            <Label className="text-sm">Employee</Label>
            <Select value={empSel || "none"} onValueChange={(v) => setEmpSel(v === "none" ? "" : v)}>
              <SelectTrigger className="w-64" data-testid="payroll-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select employee</SelectItem>
                {salaries.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.employee} ({s.role})</SelectItem>)}
              </SelectContent>
            </Select>
            <Label className="text-sm">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="payroll-month-picker" />
          </div>
          {empSel && (
            <div data-testid="employee-payslip-card">
              <div className="mb-2 text-sm font-semibold text-slate-900" data-testid="payslip-employee-name">
                {slip ? `${slip.employee} — ${slip.month}` : ""}
              </div>
              <PayslipGrid p={slip} />
            </div>
          )}
          {!empSel && <p className="py-4 text-center text-sm text-slate-500">Select an employee to view their payslip.</p>}
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="salary-card">
        <CardHeader><CardTitle className="text-base">Base Salaries — Firm 2 team</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Base Monthly Salary (₹)</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="salary-table">
              {salaries.map((s) => (
                <TableRow key={s.user_id} data-testid={`salary-row-${s.user_id}`}>
                  <TableCell className="font-medium text-slate-900">{s.employee}</TableCell>
                  <TableCell className="capitalize text-slate-500">{s.role}</TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      min="0"
                      step="100"
                      value={s.base_salary}
                      onChange={(e) => setSalaries(salaries.map((x) => (x.user_id === s.user_id ? { ...x, base_salary: e.target.value } : x)))}
                      className="ml-auto w-36 text-right"
                      data-testid={`salary-input-${s.user_id}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" disabled={savingId === s.user_id} onClick={() => saveSalary(s)} data-testid={`salary-save-${s.user_id}`}>
                      <Save className="mr-1.5 h-3.5 w-3.5" />{savingId === s.user_id ? "Saving..." : "Save"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="payslips-card">
        <CardHeader><CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
          <span>Payslips — {month} (all Firm 2 team)</span>
          <span className="flex items-center gap-2 text-xs font-normal text-slate-500">
            Paid leave quota (days/mo)
            <Input type="number" min="0" max="15" step="0.5" value={quota} onChange={(e) => setQuota(e.target.value)} className="h-8 w-20" data-testid="paid-leave-quota-input" />
            <Button size="sm" variant="outline" onClick={saveQuota} data-testid="paid-leave-quota-save">Save</Button>
          </span>
        </CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead className="text-right">Base</TableHead>
                <TableHead className="text-right">Work Days</TableHead>
                <TableHead className="text-right">Present</TableHead>
                <TableHead className="text-right">Absent</TableHead>
                <TableHead className="text-right">Half Days</TableHead>
                <TableHead className="text-right">Paid Leave</TableHead>
                <TableHead className="text-right">Daily Rate</TableHead>
                <TableHead className="text-right">Deduction</TableHead>
                <TableHead className="text-right">Net Payable</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="payslips-table">
              {slips.map((p) => (
                <TableRow key={p.user_id} data-testid={`payslip-row-${p.user_id}`}>
                  <TableCell className="font-medium text-slate-900">
                    {p.employee}
                    <span className="ml-1 text-xs capitalize text-slate-400">({p.role})</span>
                  </TableCell>
                  <TableCell className="text-right">{inr(p.base_salary)}</TableCell>
                  <TableCell className="text-right">{p.working_days}</TableCell>
                  <TableCell className="text-right text-emerald-700">{p.present}</TableCell>
                  <TableCell className="text-right">{p.absent}</TableCell>
                  <TableCell className="text-right text-amber-600">{p.half_days || 0}</TableCell>
                  <TableCell className="text-right text-sky-600">{p.paid_leave_used || 0}</TableCell>
                  <TableCell className="text-right">{inr(p.daily_rate)}</TableCell>
                  <TableCell className="text-right text-rose-600">{p.deduction ? `- ${inr(p.deduction)}` : "—"}</TableCell>
                  <TableCell className="text-right font-semibold text-slate-900">{inr(p.net_payable)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
